import axios, { AxiosInstance, AxiosError } from "axios";
import { API_URL, STORAGE_KEYS } from "../config/constants";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          const currentPath = window.location.pathname;
          const isAuthPage =
            currentPath === "/login" ||
            currentPath === "/forgot-password" ||
            currentPath === "/otp-verification" ||
            currentPath === "/reset-password";

          // Kiểm tra xem có phải là auth request không
          const isAuthRequest =
            error.config?.url?.includes("/auth/login") ||
            error.config?.url?.includes("/auth/forgot-password") ||
            error.config?.url?.includes("/auth/verify-otp") ||
            error.config?.url?.includes("/auth/reset-password") ||
            error.config?.url?.includes("/auth/register") ||
            error.config?.url?.includes("/auth/verify-2fa-login");

          // Chỉ redirect nếu:
          // 1. KHÔNG phải trang auth
          // 2. KHÔNG phải auth request (login, register, forgot-password, etc.)
          // 3. Có token trong localStorage (nghĩa là đã đăng nhập trước đó, token hết hạn)
          const hasToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

          // CHỈ redirect khi:
          // 1. KHÔNG phải trang auth (đang ở trang protected)
          // 2. KHÔNG phải auth request (login, register, forgot-password, etc.)
          // 3. Có token trong localStorage (nghĩa là đã đăng nhập trước đó, token hết hạn)
          if (!isAuthPage && !isAuthRequest && hasToken) {
            // Chỉ redirect khi token hết hạn và đang ở trang protected (không phải auth page)
            localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER_DATA);
            window.location.href = "/login";
          }
          // Nếu là auth request HOẶC đang ở auth page, KHÔNG redirect - để component xử lý error
          // Điều này đảm bảo khi login thất bại, error sẽ được hiển thị và không reload trang
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string): Promise<T> {
    const response = await this.api.get<T>(url);
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await this.api.post<T>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.api.put<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.api.delete<T>(url);
    return response.data;
  }
}

export const apiService = new ApiService();
